import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, Radio, Checkbox, Avatar, Tag, Typography, Space, Badge, Row, Col, message, Spin } from "antd";
import { SearchOutlined, ApartmentOutlined, UserOutlined, SaveOutlined, MobileOutlined, LockFilled } from "@ant-design/icons";
import { InputField, SelectInput, AntdButton } from "../../../blueplanitUtils/InputField";
import axios from "axios";
import { SERVER_URL } from "../../../databucket";

const { Title, Text } = Typography;

const MODE = { DEPARTMENT: "department", INDIVIDUAL: "individual" };
const ROUTE_FILTER = { ALL: "all", INHERITED: "inherited", CUSTOM: "custom" };
const ROUTE_TYPE = { DEPARTMENT: "department", INHERITED: "inherited", CUSTOM: "custom", NONE: "none" };

const api = () => {
  const token = localStorage.getItem("token");
  return axios.create({ baseURL: `${SERVER_URL}/api/mobile-routes`, headers: { Authorization: `Bearer ${token}` } });
};

const normalizeRoutePath = (path) => path && (path.startsWith('/') ? path : `/${path}`);
const getSubRoutePaths = (menu) => (menu.Subfields || []).flatMap(sub =>
  [normalizeRoutePath(sub.name), ...(sub.children || []).map(c => normalizeRoutePath(c.name))]
);
const filterActiveEmployees = (allEmployees) => allEmployees?.filter(emp => (emp.employeestatus || '').toLowerCase() === "active" && emp.department?.trim()) || [];
const formatEmployeeName = (emp) => `${emp?.firstname} ${emp?.lastname}`;

export default function MobileRouteManagement() {
  // Use loginUserInfo.allUsers — reliably populated on login (Phase 1)
  // Falls back to employee.AllEmployees
  const allUsersFromLogin = useSelector((s) => s.loginUserInfo?.allUsers) || [];
  const allEmployeesFromSlice = useSelector((s) => s.employee?.AllEmployees) || [];
  const allEmployees = allUsersFromLogin.length > 0 ? allUsersFromLogin : allEmployeesFromSlice;
  const NestedDatabucket = useSelector((s) => s.dataBucket?.NestedDatabucket) || [];
  const [messageApi, contextHolder] = message.useMessage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sidemenu, setSidemenu] = useState([]);
  const [commonRoutes, setCommonRoutes] = useState([]);
  const [departmentRoutes, setDepartmentRoutes] = useState({});
  const [employeeCustomRoutes, setEmployeeCustomRoutes] = useState({});
  const [origDeptRoutes, setOrigDeptRoutes] = useState({});
  const [origEmpRoutes, setOrigEmpRoutes] = useState({});

  const [mode, setMode] = useState(MODE.DEPARTMENT);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchRoutes, setSearchRoutes] = useState("");
  const [searchDepartments, setSearchDepartments] = useState("");
  const [routeFilter, setRouteFilter] = useState(ROUTE_FILTER.ALL);

  const employees = useMemo(() => filterActiveEmployees(allEmployees), [allEmployees]);
  const departments = useMemo(() => {
    const depts = [...new Set(employees.map(e => e.department).filter(Boolean))].sort();
    return depts.map(d => ({ name: d, employeeCount: employees.filter(e => e.department === d).length }));
  }, [employees]);

  const departmentEmployees = useMemo(() => employees.filter(e => e.department === selectedDepartment), [employees, selectedDepartment]);
  const currentEmployee = useMemo(() => employees.find(e => e.id === selectedEmployee), [employees, selectedEmployee]);

  // Load data
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api().get('/access');
        setSidemenu(data.sidemenu || []);
        setCommonRoutes(data.commonRoutes || []);

        const deptMap = {};
        const origDept = {};
        for (const d of (data.departmentAccess || [])) {
          deptMap[d.department] = [...(d.routes || [])];
          origDept[d.department] = [...(d.routes || [])];
        }
        setDepartmentRoutes(deptMap);
        setOrigDeptRoutes(origDept);

        const empMap = {};
        const origEmp = {};
        for (const e of (data.employeeAccess || [])) {
          empMap[e.employeeId] = [...(e.routes || [])];
          origEmp[e.employeeId] = [...(e.routes || [])];
        }
        setEmployeeCustomRoutes(empMap);
        setOrigEmpRoutes(origEmp);

      } catch (err) {
        messageApi.error("Failed to load mobile access data");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (departments.length && !departments.find(d => d.name === selectedDepartment)) {
      setSelectedDepartment(departments[0].name);
    }
  }, [departments, selectedDepartment]);

  useEffect(() => {
    if (departmentEmployees.length && !departmentEmployees.find(e => e.id === selectedEmployee)) {
      setSelectedEmployee(departmentEmployees[0]?.id || null);
    }
  }, [departmentEmployees]);

  const isCommonRoute = (path) => commonRoutes.includes(normalizeRoutePath(path));

  const getInheritedRoutes = useCallback((employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? (departmentRoutes[emp.department] || []) : [];
  }, [employees, departmentRoutes]);

  const getAllEmployeeRoutes = useCallback((employeeId) => {
    const inherited = getInheritedRoutes(employeeId);
    const custom = employeeCustomRoutes[employeeId] || [];
    return [...new Set([...commonRoutes, ...inherited, ...custom])];
  }, [getInheritedRoutes, employeeCustomRoutes, commonRoutes]);

  const getRouteStatus = useCallback((menu, subfield = null) => {
    if (subfield) {
      const routePath = normalizeRoutePath(subfield.name);
      if (isCommonRoute(routePath)) return { checked: true, type: "common" };

      if (mode === MODE.DEPARTMENT) {
        const routes = departmentRoutes[selectedDepartment] || [];
        return { checked: routes.includes(routePath), type: ROUTE_TYPE.DEPARTMENT };
      }
      if (!selectedEmployee) return { checked: false, type: ROUTE_TYPE.NONE };
      const inherited = getInheritedRoutes(selectedEmployee);
      const custom = employeeCustomRoutes[selectedEmployee] || [];
      if (inherited.includes(routePath)) return { checked: true, type: ROUTE_TYPE.INHERITED };
      if (custom.includes(routePath)) return { checked: true, type: ROUTE_TYPE.CUSTOM };
      return { checked: false, type: ROUTE_TYPE.NONE };
    } else {
      const subRoutePaths = getSubRoutePaths(menu);
      if (!subRoutePaths.length) return { checked: false, type: ROUTE_TYPE.NONE };
      const allCommon = subRoutePaths.every(p => isCommonRoute(p));
      if (allCommon) return { checked: true, type: "common" };

      if (mode === MODE.DEPARTMENT) {
        const routes = [...commonRoutes, ...(departmentRoutes[selectedDepartment] || [])];
        return { checked: subRoutePaths.every(p => routes.includes(p)), type: ROUTE_TYPE.DEPARTMENT };
      }
      if (!selectedEmployee) return { checked: false, type: ROUTE_TYPE.NONE };
      const allRoutes = getAllEmployeeRoutes(selectedEmployee);
      return { checked: subRoutePaths.every(p => allRoutes.includes(p)), type: ROUTE_TYPE.DEPARTMENT };
    }
  }, [mode, selectedDepartment, selectedEmployee, departmentRoutes, employeeCustomRoutes, commonRoutes, getInheritedRoutes, getAllEmployeeRoutes]);

  const handleToggle = useCallback((menu, subfield = null) => {
    // If subfield has children (it's a hub), include hub + all children paths
    let routePaths;
    if (subfield) {
      routePaths = [normalizeRoutePath(subfield.name)];
      if (subfield.children?.length) {
        routePaths.push(...subfield.children.map(c => normalizeRoutePath(c.name)));
      }
    } else {
      // Toggle entire category — get all routes including nested children
      routePaths = (menu.Subfields || []).flatMap(sub =>
        [normalizeRoutePath(sub.name), ...(sub.children || []).map(c => normalizeRoutePath(c.name))]
      );
    }
    if (!routePaths.length) return;
    // Can't toggle common routes
    const toggleable = routePaths.filter(p => !isCommonRoute(p));
    if (!toggleable.length) { messageApi.info("Common screens cannot be toggled"); return; }

    if (mode === MODE.DEPARTMENT) {
      setDepartmentRoutes(prev => {
        const current = prev[selectedDepartment] || [];
        const allSelected = toggleable.every(p => current.includes(p));
        const updated = allSelected ? current.filter(p => !toggleable.includes(p)) : [...new Set([...current, ...toggleable])];
        return { ...prev, [selectedDepartment]: updated };
      });
    } else {
      if (!selectedEmployee) return;
      const inherited = getInheritedRoutes(selectedEmployee);
      if (toggleable.some(p => inherited.includes(p))) {
        messageApi.info("Inherited routes from department cannot be toggled here");
        return;
      }
      setEmployeeCustomRoutes(prev => {
        const current = prev[selectedEmployee] || [];
        const allSelected = toggleable.every(p => current.includes(p));
        const updated = allSelected ? current.filter(p => !toggleable.includes(p)) : [...new Set([...current, ...toggleable])];
        return { ...prev, [selectedEmployee]: updated };
      });
    }
  }, [mode, selectedDepartment, selectedEmployee, commonRoutes, getInheritedRoutes, messageApi]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (mode === MODE.DEPARTMENT) {
        const original = origDeptRoutes[selectedDepartment] || [];
        const current = departmentRoutes[selectedDepartment] || [];
        const routesToAdd = current.filter(r => !original.includes(r));
        const routesToRemove = original.filter(r => !current.includes(r));
        if (!routesToAdd.length && !routesToRemove.length) { messageApi.info("No changes"); setSaving(false); return; }
        await api().put('/department', { department: selectedDepartment, routesToAdd, routesToRemove, changedBy: 'admin' });
        setOrigDeptRoutes(prev => ({ ...prev, [selectedDepartment]: [...current] }));
        messageApi.success(`Saved for ${selectedDepartment} department`);
      } else {
        const original = origEmpRoutes[selectedEmployee] || [];
        const current = employeeCustomRoutes[selectedEmployee] || [];
        const routesToAdd = current.filter(r => !original.includes(r));
        const routesToRemove = original.filter(r => !current.includes(r));
        if (!routesToAdd.length && !routesToRemove.length) { messageApi.info("No changes"); setSaving(false); return; }
        await api().put('/employee', {
          employeeId: selectedEmployee,
          employeeName: formatEmployeeName(currentEmployee),
          employeeDepartment: currentEmployee?.department,
          routesToAdd, routesToRemove, changedBy: 'admin'
        });
        setOrigEmpRoutes(prev => ({ ...prev, [selectedEmployee]: [...current] }));
        messageApi.success(`Saved for ${formatEmployeeName(currentEmployee)}`);
      }
    } catch (err) {
      messageApi.error(err.response?.data?.error || "Failed to save");
    } finally { setSaving(false); }
  }, [mode, selectedDepartment, selectedEmployee, departmentRoutes, employeeCustomRoutes, origDeptRoutes, origEmpRoutes, currentEmployee, messageApi]);

  const hasUnsavedChanges = useMemo(() => {
    if (mode === MODE.DEPARTMENT) {
      const orig = origDeptRoutes[selectedDepartment] || [];
      const curr = departmentRoutes[selectedDepartment] || [];
      return JSON.stringify([...orig].sort()) !== JSON.stringify([...curr].sort());
    }
    const orig = origEmpRoutes[selectedEmployee] || [];
    const curr = employeeCustomRoutes[selectedEmployee] || [];
    return JSON.stringify([...orig].sort()) !== JSON.stringify([...curr].sort());
  }, [mode, selectedDepartment, selectedEmployee, departmentRoutes, employeeCustomRoutes, origDeptRoutes, origEmpRoutes]);

  const filteredMenu = sidemenu.filter(menu => {
    if (!searchRoutes) return true;
    const lower = searchRoutes.toLowerCase();
    return menu.name.toLowerCase().includes(lower) || menu.Subfields?.some(sub => sub.displayName.toLowerCase().includes(lower));
  });

  const filteredDepartments = !searchDepartments ? departments : departments.filter(d => d.name.toLowerCase().includes(searchDepartments.toLowerCase()));

  const assignedCount = mode === MODE.DEPARTMENT
    ? [...new Set([...commonRoutes, ...(departmentRoutes[selectedDepartment] || [])])].length
    : selectedEmployee ? getAllEmployeeRoutes(selectedEmployee).length : 0;

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}><Spin size="large" /></div>;

  return (
    <div style={styles.container}>
      {contextHolder}
      <div style={styles.headerRow}>
        <div>
          <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
            <MobileOutlined style={{ marginRight: 8 }} /> Mobile Route Management
          </Title>
          <Text type="secondary">Manage mobile app screen access by department or individual employee</Text>
        </div>
        <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} buttonStyle="solid">
          <Radio.Button value={MODE.DEPARTMENT}><ApartmentOutlined style={{ marginRight: 8 }} />Department</Radio.Button>
          <Radio.Button value={MODE.INDIVIDUAL}><UserOutlined style={{ marginRight: 8 }} />Individual</Radio.Button>
        </Radio.Group>
      </div>

      {mode === MODE.INDIVIDUAL && (
        <Card style={{ ...styles.mainCard, marginBottom: 20 }}>
          <Row gutter={24}>
            <Col span={12}>
              <SelectInput label="Department" style={{ width: "100%" }} value={selectedDepartment}
                onChange={(v) => { setSelectedDepartment(v); const de = employees.filter(e => e.department === v); setSelectedEmployee(de[0]?.id || null); }}
                options={departments.map(d => ({ label: `${d.name} (${d.employeeCount})`, value: d.name }))} />
            </Col>
            <Col span={12}>
              <SelectInput label="Employee" style={{ width: "100%" }} value={selectedEmployee} onChange={setSelectedEmployee}
                options={departmentEmployees.map(emp => ({ label: `${emp.firstname} ${emp.lastname} - ${emp.designation || emp.role}`, value: emp.id }))} />
            </Col>
          </Row>
        </Card>
      )}

      <Row gutter={20}>
        {mode === MODE.DEPARTMENT && (
          <Col span={6}>
            <Card style={styles.deptCard} bodyStyle={{ padding: 16 }}>
              <InputField placeholder="Search departments..." prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                style={{ marginBottom: 16 }} value={searchDepartments} onChange={(e) => setSearchDepartments(e.target.value)} allowClear />
              {filteredDepartments.map(dept => (
                <div key={dept.name} style={styles.deptItem(selectedDepartment === dept.name)} onClick={() => setSelectedDepartment(dept.name)}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><ApartmentOutlined /><Text strong style={{ color: "inherit" }}>{dept.name}</Text></div>
                    <Text style={{ fontSize: 12, color: selectedDepartment === dept.name ? "rgba(255,255,255,0.8)" : "#8c8c8c" }}>{dept.employeeCount} employees</Text>
                  </div>
                  <Badge count={(departmentRoutes[dept.name] || []).length + commonRoutes.length}
                    style={{ backgroundColor: selectedDepartment === dept.name ? "rgba(255,255,255,0.25)" : "#f0f0f0", color: selectedDepartment === dept.name ? "#fff" : "#595959", boxShadow: "none" }} />
                </div>
              ))}
            </Card>
          </Col>
        )}

        <Col span={mode === MODE.DEPARTMENT ? 18 : 24}>
          <Card style={styles.mainCard} bodyStyle={{ padding: 0 }}>
            {mode === MODE.INDIVIDUAL && currentEmployee && (
              <div style={styles.empHeader}>
                <Space size={16}>
                  <Avatar size={48} src={currentEmployee.image} style={{ background: "#8B5CF6" }}>{currentEmployee.firstname?.charAt(0)}</Avatar>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>{formatEmployeeName(currentEmployee)}</Title>
                    <Text type="secondary">{currentEmployee.designation || currentEmployee.role} • {currentEmployee.department}</Text>
                  </div>
                </Space>
                <Space>
                  <Tag color="green">{commonRoutes.length} Common</Tag>
                  <Tag color="blue">{getInheritedRoutes(selectedEmployee).length} Inherited</Tag>
                  <Tag color="gold">{(employeeCustomRoutes[selectedEmployee] || []).length} Custom</Tag>
                </Space>
              </div>
            )}

            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Title level={5} style={{ margin: 0 }}>Mobile Screens — {assignedCount} assigned</Title>
                  <Text type="secondary"><LockFilled style={{ color: "#10B981", marginRight: 4 }} />Common screens (green) are always ON for all users</Text>
                </div>
                <InputField placeholder="Search screens..." prefix={<SearchOutlined />} style={{ width: 250 }}
                  value={searchRoutes} onChange={(e) => setSearchRoutes(e.target.value)} allowClear />
              </div>
            </div>

            <div style={{ padding: "16px 24px", maxHeight: 500, overflowY: "auto" }}>
              <Row gutter={[16, 16]}>
                {filteredMenu.map((menu, idx) => {
                  const mainStatus = getRouteStatus(menu, null);
                  const isCommonSection = menu.name === 'Common';
                  return (
                    <Col xs={24} sm={12} md={12} lg={6} key={`m-${menu.name}-${idx}`}>
                      <div style={styles.routeItem(mainStatus.checked, mainStatus.type)}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {isCommonSection ? (
                                <LockFilled style={{ color: "#10B981" }} />
                              ) : (
                                <Checkbox checked={mainStatus.checked} onChange={() => handleToggle(menu, null)} />
                              )}
                              <Text strong style={{ fontSize: 14 }}>{menu.name}</Text>
                            </div>
                            {isCommonSection && <Tag color="green" style={{ fontSize: 10 }}>Always ON</Tag>}
                          </div>

                          {mainStatus.type !== "common" && mainStatus.type !== ROUTE_TYPE.NONE && mode === MODE.INDIVIDUAL && mainStatus.checked && (
                            <Tag color={mainStatus.type === ROUTE_TYPE.INHERITED ? "blue" : "gold"} style={{ fontSize: 10, width: "fit-content", marginLeft: 28 }}>
                              {mainStatus.type === ROUTE_TYPE.INHERITED ? "Inherited" : "Custom"}
                            </Tag>
                          )}

                          <div style={{ marginLeft: 28, marginTop: 4 }}>
                            {menu.Subfields?.map((sub, si) => {
                              const subStatus = getRouteStatus(menu, sub);
                              const isCommon = isCommonRoute(sub.name);
                              const hasChildren = sub.children?.length > 0;
                              return (
                                <div key={`sub-${sub.name}-${si}`} style={{ marginBottom: hasChildren ? 10 : 6 }}>
                                  {/* Sub-route row */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    {isCommon ? (
                                      <LockFilled style={{ color: "#10B981", fontSize: 12 }} />
                                    ) : (
                                      <Checkbox checked={subStatus.checked} onChange={() => handleToggle(menu, sub)} />
                                    )}
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <Text style={{ fontSize: 12, fontWeight: hasChildren ? 600 : 400, color: isCommon ? "#10B981" : undefined }}>{sub.displayName}</Text>
                                        {hasChildren && <Tag style={{ fontSize: 9, padding: "0 4px", margin: 0 }}>{sub.children.length} screens</Tag>}
                                        {mode === MODE.INDIVIDUAL && subStatus.checked && !isCommon && !hasChildren && (
                                          <Tag color={subStatus.type === ROUTE_TYPE.INHERITED ? "blue" : "gold"} style={{ fontSize: 9, padding: "0 4px", margin: 0 }}>
                                            {subStatus.type === ROUTE_TYPE.INHERITED ? "Dept" : "Custom"}
                                          </Tag>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {/* Children (3rd level — individual screens inside a hub) */}
                                  {hasChildren && (
                                    <div style={{ marginLeft: 28, marginTop: 4, borderLeft: "2px solid #e5e7eb", paddingLeft: 12 }}>
                                      {sub.children.map((child, ci) => {
                                        const childStatus = getRouteStatus(menu, child);
                                        return (
                                          <div key={`child-${child.name}-${ci}`} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                            <Checkbox checked={childStatus.checked} onChange={() => handleToggle(menu, child)} />
                                            <div style={{ flex: 1 }}>
                                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <Text style={{ fontSize: 11 }}>{child.displayName}</Text>
                                                {mode === MODE.INDIVIDUAL && childStatus.checked && (
                                                  <Tag color={childStatus.type === ROUTE_TYPE.INHERITED ? "blue" : "gold"} style={{ fontSize: 8, padding: "0 3px", margin: 0 }}>
                                                    {childStatus.type === ROUTE_TYPE.INHERITED ? "Dept" : "Custom"}
                                                  </Tag>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>

            <div style={styles.footer}>
              <Space>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: hasUnsavedChanges ? "#faad14" : "#52c41a" }} />
                <Text type="secondary">
                  {mode === MODE.DEPARTMENT ? `Applies to all employees in ${selectedDepartment}` : `Custom overrides for ${formatEmployeeName(currentEmployee)}`}
                </Text>
                {hasUnsavedChanges && <Tag color="warning">Unsaved</Tag>}
              </Space>
              <AntdButton type="primary" size="large" icon={<SaveOutlined />} onClick={handleSave}
                loading={saving} disabled={!hasUnsavedChanges || saving} label={mode === MODE.DEPARTMENT ? "Save Department Access" : "Save Employee Access"} />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", background: "#f5f5f5", padding: "24px" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  mainCard: { borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "none" },
  deptCard: { borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", height: "100%", border: "none" },
  deptItem: (sel) => ({ padding: "12px 16px", marginBottom: 8, borderRadius: 8, cursor: "pointer", background: sel ? "#8B5CF6" : "#fff", color: sel ? "#fff" : "#333", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.2s" }),
  routeItem: (checked, type) => ({ padding: 12, height: "100%", borderRadius: 10, background: type === "common" ? "#f0fdf4" : checked ? (type === ROUTE_TYPE.CUSTOM ? "#fffbe6" : "#f0f0ff") : "#fff", border: `1px solid ${type === "common" ? "#bbf7d0" : checked ? (type === ROUTE_TYPE.CUSTOM ? "#ffe58f" : "#d6d6ff") : "#f0f0f0"}`, transition: "all 0.2s" }),
  empHeader: { padding: 20, background: "#fafafa", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" },
  footer: { padding: "16px 24px", background: "#fafafa", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: "0 0 12px 12px" },
};
